import React, { useState } from 'react'
import { Plus } from 'lucide-react'
import type { WatchlistList } from '../../services/watchlist'

interface WatchlistCreateListInlineProps {
  onCreate: (name: string) => Promise<WatchlistList>
  onCreated?: (list: WatchlistList) => void
  /** Compact styling for narrow dropdowns. */
  compact?: boolean
  inputId?: string
}

/**
 * Inline affordance to create a watchlist list: "+ Ny liste" expands to name + submit.
 */
const WatchlistCreateListInline: React.FC<WatchlistCreateListInlineProps> = ({
  onCreate,
  onCreated,
  compact = false,
  inputId = 'new-watchlist-list',
}) => {
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  function cancel() {
    setCreating(false)
    setName('')
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return
    setBusy(true)
    try {
      const list = await onCreate(trimmed)
      setName('')
      setCreating(false)
      onCreated?.(list)
    } finally {
      setBusy(false)
    }
  }

  if (creating) {
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-2 min-w-0">
        <label htmlFor={inputId} className="sr-only">
          Ny liste
        </label>
        <input
          id={inputId}
          type="text"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              cancel()
            }
          }}
          placeholder="Listenavn"
          disabled={busy}
          className={`border-b-2 border-rule focus:border-ink bg-paper px-0 py-1 text-ink placeholder:text-ink-300 focus:outline-none min-w-0 w-full ${
            compact ? 'text-xs' : 'text-sm'
          }`}
        />
        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="bg-ink hover:bg-ink-700 disabled:bg-ink-300 text-paper text-xs font-semibold rounded-md px-2.5 py-1 transition-colors rounded-sm"
          >
            Opret
          </button>
          <button
            type="button"
            onClick={cancel}
            disabled={busy}
            className="text-xs text-ink-500 hover:text-ink"
          >
            Annuller
          </button>
        </div>
      </form>
    )
  }

  return (
    <button
      type="button"
      onClick={() => setCreating(true)}
      className="inline-flex items-center gap-1 text-[13px] font-semibold text-ink-500 hover:text-ink"
    >
      <Plus className="h-3.5 w-3.5" aria-hidden />
      Ny liste
    </button>
  )
}

export default WatchlistCreateListInline
